import {
  downloadFile,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import { VersionedFilePreview } from "@/components/features/files/VersionedFilePreview";
import { queryKeys } from "@/lib/data/static/query-keys";
import { Badge, Button, LoadingSpinner } from "@/components/ui";
import { FileThumbnail } from "@/components/ui/file-thumbnail";
import { PDFJsViewer } from "@/components/ui/PDFJsViewer/PDFJsViewer";
import { useOptionalUser } from "@/components/UserProvider";
import { getItemFromPresignedUrlOptions } from "@/lib/data/react-query/s3";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import {
  getFileNameParts,
  getIsFileImage,
  handleDownload,
  downloadFileLink,
  handlePreviewFile,
} from "@/lib/utils/matterFile";
import { useQuery } from "@tanstack/react-query";
import { renderAsync } from "docx-preview";
import { Download, Pencil, SquareArrowOutUpRight, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const CardWrapper = ({
  children,
  thumbnail,
  className,
}: {
  children: React.ReactNode;
  thumbnail?: boolean;
  className?: string;
}) => {
  if (thumbnail) {
    return <>{children}</>;
  }
  return (
    <div
      className={cn(
        "bg-card flex h-full w-full flex-col rounded-md shadow-md",
        className,
      )}
    >
      {children}
    </div>
  );
};

export type FilePreviewVersion = { fileId: string; versionId: string | null };

export type FileViewerProps = {
  versionControlsContainer?: HTMLElement | null;
  onVersionChange?: (selection: FilePreviewVersion) => void;
  file:
    | { fileName: string; presignedURLKey: string; fileId?: string }
    | { file: File; url: string };
  fileType:
    | "matter-file"
    | "firm-file"
    | "matter-template"
    | "matter-signature-request-file"
    | "firm-signature-request-file"
    | "matter-share-folder-file";
  contentType?: string;
  showDownload?: boolean;
  showTitle?: boolean;
  handleRemoveFileUpload?: () => void;
  imageClassName?: string;
  imageLoadingClassName?: string;
  viewerClassName?: string;
  wrapperClassName?: string;
  showDocxModeToggle?: boolean;
  canEditDocx?: boolean;
  onDocxEdit?: () => void;
  fileInWrapper?: boolean;
  thumbnail?: boolean;
  unauthenticated?: boolean;
  shareFolderPasscode?: string;
  presignedUrl?: string;
  footerClassName?: string;
  onDocxScrollContainerReady?: (container: HTMLDivElement | null) => void;
  onDocxRendered?: () => void;
  onEdit?: () => void;
  compactDocxPreview?: boolean;
  showEditOverlay?: boolean;
  canEditOverlay?: boolean;
  hidePreviewOverlayControls?: boolean;
  renderPageOverlay?: (props: {
    pageHeight: number;
    pageNumber: number;
    pageWidth: number;
    scale: number;
  }) => React.ReactNode;
  onLoadingStateChange?: (isLoading: boolean) => void;
};

export function FileViewer(props: FileViewerProps) {
  const fileId =
    "fileId" in props.file &&
    !props.unauthenticated &&
    !props.thumbnail &&
    !props.presignedUrl &&
    (props.fileType === "matter-file" || props.fileType === "firm-file")
      ? props.file.fileId
      : undefined;
  return fileId ? (
    <VersionedFilePreview key={fileId} fileId={fileId} viewerProps={props} />
  ) : (
    <FileViewerContent {...props} />
  );
}

export function FileViewerContent({
  file,
  fileType,
  contentType,
  showDownload = true,
  showTitle = true,
  handleRemoveFileUpload,
  imageClassName,
  imageLoadingClassName,
  viewerClassName,
  wrapperClassName,
  showDocxModeToggle,
  canEditDocx,
  onDocxEdit,
  fileInWrapper = true,
  thumbnail,
  unauthenticated = false,
  shareFolderPasscode,
  presignedUrl,
  footerClassName,
  onDocxScrollContainerReady,
  onDocxRendered,
  onEdit,
  compactDocxPreview = false,
  showEditOverlay = false,
  canEditOverlay = false,
  hidePreviewOverlayControls = false,
  renderPageOverlay,
  onLoadingStateChange,
}: FileViewerProps) {
  const useCompactDocxPreview = compactDocxPreview;
  const user = useOptionalUser();
  const hasActionPermission =
    user?.hasActionPermission ??
    (() => {
      return false;
    });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const docxScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastRenderedUrlRef = useRef<string | null>(null);
  const isRenderingRef = useRef(false);
  const docxRenderGenerationRef = useRef(0);
  const isUploadedFile = "file" in file;
  const [pdfError, setPdfError] = useState<string | null>(null);

  const fileName = isUploadedFile ? file.file.name : file.fileName;
  const canonicalId =
    !isUploadedFile &&
    !unauthenticated &&
    (fileType === "matter-file" || fileType === "firm-file")
      ? file.fileId
      : undefined;
  const currentDownload = useQuery({
    queryKey: [
      queryKeys.files,
      canonicalId,
      "download",
      isUploadedFile ? undefined : file.presignedURLKey,
    ],
    queryFn: () => {
      if (!canonicalId) throw new Error("A file must be selected.");
      return downloadFile(canonicalId);
    },
    enabled: !!canonicalId && !presignedUrl,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 4 * 60 * 1000,
  });
  const {
    data: fileFromPresignedURLData,
    isLoading: isLoadingFileUrl,
    isError: isErrorFile,
  } = useQuery({
    ...getItemFromPresignedUrlOptions(
      fileType,
      isUploadedFile ? "" : file.presignedURLKey || "",
      { unauthenticated, shareFolderPasscode },
    ),
    enabled:
      !canonicalId &&
      !presignedUrl &&
      !isUploadedFile &&
      Boolean(file.presignedURLKey),
  });

  const fileUrl = presignedUrl
    ? presignedUrl
    : canonicalId
      ? currentDownload.data?.url
      : unauthenticated
        ? fileFromPresignedURLData?.url
        : isUploadedFile
          ? file.url
          : fileFromPresignedURLData?.url;

  const sourceContentType = contentType || currentDownload.data?.contentType;
  const previewContentType =
    sourceContentType && sourceContentType !== "application/octet-stream"
      ? sourceContentType
      : undefined;
  const isImage = previewContentType
    ? previewContentType.startsWith("image/")
    : getIsFileImage(fileName);
  const isPdf = previewContentType
    ? previewContentType === "application/pdf"
    : getFileNameParts(fileName).fileExtension === "pdf";
  const isVideo = previewContentType
    ? previewContentType.startsWith("video/")
    : ["mp4", "webm", "ogg", "mov", "avi", "wmv", "flv", "mkv"].includes(
        getFileNameParts(fileName).fileExtension,
      );
  const isAudio = previewContentType
    ? previewContentType.startsWith("audio/")
    : ["mp3", "wav", "ogg", "flac", "aac"].includes(
        getFileNameParts(fileName).fileExtension,
      );
  const isDocument = previewContentType
    ? previewContentType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    : ["docx"].includes(getFileNameParts(fileName).fileExtension);

  const pdfViewerSrc = isPdf ? fileUrl : fileUrl;
  const isError =
    (!presignedUrl && (canonicalId ? currentDownload.isError : isErrorFile)) ||
    Boolean(pdfError);
  const sourceError = canonicalId ? currentDownload.isError : isErrorFile;
  const isLoading =
    !presignedUrl &&
    !isUploadedFile &&
    !sourceError &&
    (canonicalId ? currentDownload.isLoading : isLoadingFileUrl);
  const errorMessage =
    pdfError ??
    (sourceError
      ? fileOperationMessage(currentDownload.error, "Unable to load file.")
      : "");

  useEffect(() => {
    onLoadingStateChange?.(isLoading);
  }, [isLoading, onLoadingStateChange]);

  useEffect(() => {
    if (!isPdf) {
      setPdfError(null);
      return;
    }

    if (presignedUrl) {
      setPdfError(null);
      return;
    }

    if (isUploadedFile) {
      setPdfError(null);
      return;
    }

    if (!fileFromPresignedURLData) {
      setPdfError(null);
      return;
    }

    if (!fileFromPresignedURLData.ok) {
      setPdfError("Unable to load PDF preview.");
      return;
    }

    setPdfError(null);
    return;
  }, [file, fileFromPresignedURLData, isPdf, isUploadedFile, presignedUrl]);

  const renderDocxFile = useCallback(
    async (file: File) => {
      if (!containerRef.current) return;

      const generation = ++docxRenderGenerationRef.current;
      const targetContainer = containerRef.current;
      try {
        const arrayBuffer = await file.arrayBuffer();
        if (generation !== docxRenderGenerationRef.current) return;

        const stagingContainer =
          targetContainer.ownerDocument.createElement("div");
        await renderAsync(arrayBuffer, stagingContainer, stagingContainer, {
          className: "docx-viewer",
          inWrapper: fileInWrapper,
          ignoreWidth: useCompactDocxPreview,
          ignoreHeight: useCompactDocxPreview,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
        });
        if (
          generation !== docxRenderGenerationRef.current ||
          containerRef.current !== targetContainer
        ) {
          return;
        }

        targetContainer.replaceChildren(...stagingContainer.childNodes);
        onDocxRendered?.();
      } catch (err) {
        console.error("Error rendering docx:", err);
      }
    },
    [containerRef, fileInWrapper, onDocxRendered, useCompactDocxPreview],
  );

  const fetchDocxFromUrl = useCallback(async () => {
    if (!fileUrl) return;
    if (isRenderingRef.current) return;
    if (
      lastRenderedUrlRef.current === fileUrl &&
      containerRef.current &&
      containerRef.current.childElementCount > 0
    ) {
      return;
    }

    isRenderingRef.current = true;
    try {
      const urlParts = fileUrl.split("/");
      const urlFileName = urlParts[urlParts.length - 1];
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch file: ${response.status} ${response.statusText}`,
        );
      }
      const contentType = response.headers.get("content-type");
      if (
        contentType &&
        !contentType.includes(
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ) &&
        !contentType.includes("application/octet-stream")
      ) {
        console.warn(
          `Warning: Content type "${contentType}" may not be a DOCX file`,
        );
      }
      const blob = await response.blob();
      const file = new File([blob], urlFileName || "document.docx", {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      await renderDocxFile(file);
      lastRenderedUrlRef.current = fileUrl;
    } catch (err) {
      console.error("Error fetching DOCX from URL:", err);
    } finally {
      isRenderingRef.current = false;
    }
  }, [fileUrl, renderDocxFile]);

  useEffect(() => {
    if (isDocument && containerRef.current) {
      if (!isUploadedFile && fileUrl) {
        void fetchDocxFromUrl();
      } else if (isUploadedFile) {
        void renderDocxFile(file.file);
      }
    }
  }, [
    isDocument,
    fileUrl,
    fetchDocxFromUrl,
    renderDocxFile,
    isUploadedFile,
    file,
  ]);

  useEffect(() => {
    if (!isDocument) {
      onDocxScrollContainerReady?.(null);
      return;
    }

    const scrollContainer = docxScrollContainerRef.current;
    if (!scrollContainer) return;

    onDocxScrollContainerReady?.(scrollContainer);

    return () => {
      onDocxScrollContainerReady?.(null);
    };
  }, [isDocument, onDocxScrollContainerReady, fileUrl]);

  const pdfPreview = isPdf && pdfViewerSrc && (
    <PDFJsViewer
      src={pdfViewerSrc}
      className={cn(
        "h-full min-h-[300px] w-full grow bg-white",
        thumbnail && "min-h-0",
        imageClassName,
      )}
      renderPageOverlay={renderPageOverlay}
    />
  );
  const docxPreview = isDocument && fileUrl && (
    <div
      className={cn(
        "mt-6 flex h-full min-h-0 w-full min-w-0 flex-col",
        useCompactDocxPreview && "mt-0 h-auto min-h-full w-full",
      )}
    >
      <div
        ref={docxScrollContainerRef}
        className={cn(
          "bg-card min-h-0 w-full min-w-0 flex-1 overflow-x-auto overflow-y-auto rounded-lg border",
          useCompactDocxPreview &&
            "min-h-full flex-none overflow-x-hidden overflow-y-auto rounded-lg border bg-slate-100/70 p-3",
        )}
      >
        <div
          className={cn(useCompactDocxPreview && "flex w-full justify-center")}
        >
          <div
            ref={containerRef}
            className={cn(
              "docx-container",
              !useCompactDocxPreview && "min-w-max",
              useCompactDocxPreview &&
                "w-full max-w-full min-w-0 [&_.docx-viewer_table]:!w-full [&_.docx-viewer_table]:!max-w-full [&_.docx-viewer-wrapper]:!flex [&_.docx-viewer-wrapper]:!w-full [&_.docx-viewer-wrapper]:!max-w-full [&_.docx-viewer-wrapper]:!items-stretch [&_.docx-viewer-wrapper]:!bg-transparent [&_.docx-viewer-wrapper]:!p-0 [&_section.docx-viewer]:!mx-0 [&_section.docx-viewer]:!mb-0 [&_section.docx-viewer]:!min-h-0 [&_section.docx-viewer]:!w-full [&_section.docx-viewer]:!max-w-full [&_section.docx-viewer]:!overflow-visible [&_section.docx-viewer]:!shadow-none [&_section.docx-viewer>article]:!w-full [&_section.docx-viewer>article]:!max-w-full",
            )}
          />
        </div>
      </div>
    </div>
  );

  return (
    <CardWrapper thumbnail={thumbnail} className={wrapperClassName}>
      <div
        className={cn(
          "bg-muted/20 relative flex min-w-0 overflow-hidden",
          isPdf
            ? "items-stretch justify-stretch"
            : "items-center justify-center",
          useCompactDocxPreview &&
            isDocument &&
            "bg-card items-start justify-stretch overflow-x-hidden overflow-y-auto p-4",
          viewerClassName,
          !thumbnail && "grow",
        )}
      >
        {!hidePreviewOverlayControls &&
          ((isDocument && showDocxModeToggle) || showEditOverlay) && (
            <div
              className={cn(
                "bg-card absolute right-4 z-10 flex items-center gap-2 rounded-full border px-2 py-1 shadow-sm",
                isPdf ? "top-16" : "top-4",
              )}
            >
              {(isDocument && showDocxModeToggle) || showEditOverlay ? (
                <Badge variant="outline" className="px-2 py-0.5 text-[11px]">
                  View mode
                </Badge>
              ) : null}
              {(isDocument
                ? canEditDocx && onDocxEdit
                : canEditOverlay && onEdit) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (isDocument) {
                      onDocxEdit?.();
                      return;
                    }
                    onEdit?.();
                  }}
                  title="Edit"
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
            </div>
          )}
        {isLoading ? (
          <div className={cn("px-4 py-12 text-center", imageLoadingClassName)}>
            <div className="border-primary mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-t-2 border-b-2"></div>
            <p className="text-muted-foreground">Loading file...</p>
          </div>
        ) : isError ? (
          <div className="text-destructive px-4 py-12 text-center">
            <p>{errorMessage}</p>
            {canonicalId && (
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => {
                  setPdfError(null);
                  void currentDownload.refetch();
                }}
              >
                Retry preview
              </Button>
            )}
          </div>
        ) : isImage && fileUrl ? (
          <img
            src={fileUrl || "/placeholder.svg"}
            alt={fileName}
            className={cn(
              "h-full w-full max-w-full object-contain",
              imageClassName,
            )}
            crossOrigin="anonymous"
          />
        ) : thumbnail && (isPdf || isDocument) ? (
          <FileThumbnail
            file={{
              name: fileName,
              type: isPdf
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            }}
            className="h-full w-full border-0 bg-transparent"
            previewClassName="bg-white"
            previewContent={isPdf ? pdfPreview : docxPreview}
            isLoading={isLoading}
            hasError={isError}
          />
        ) : isVideo && fileUrl ? (
          <video
            className="block h-auto w-full"
            src={fileUrl}
            controls
            width={1000}
            height={1000}
            crossOrigin="anonymous"
          />
        ) : isAudio && fileUrl ? (
          <audio
            src={fileUrl}
            controls
            className="w-full"
            crossOrigin="anonymous"
          />
        ) : pdfPreview ? (
          pdfPreview
        ) : docxPreview ? (
          docxPreview
        ) : (
          <div className="px-4 py-12 text-center">
            <p className="text-muted-foreground">
              Preview not available for this file type
            </p>
          </div>
        )}
      </div>
      <div
        className={cn(
          "mx-2 flex items-center justify-between py-4",
          footerClassName,
        )}
      >
        <div className="flex items-center gap-2">
          {showTitle && <h3 className="font-medium">{fileName}</h3>}
          {handleRemoveFileUpload &&
            hasActionPermission(
              fileType === "firm-file"
                ? ACTION_PERMISSION_KEYS.firmFile
                : ACTION_PERMISSION_KEYS.matterFile,
              ACTION_PERMISSION_VALUES.delete,
            ) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-muted-foreground hover:text-foreground mx-0 h-6 w-6"
                onClick={handleRemoveFileUpload}
                data-testid="remove-file-upload-button"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
        </div>
        <div className="flex gap-x-2">
          {showDownload && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-muted-foreground hover:text-foreground mx-0 h-6 w-6"
              onClick={async (e) => {
                e.preventDefault();
                if (fileUrl && fileName) {
                  if (canonicalId) {
                    try {
                      const response = await downloadFile(canonicalId);
                      if (fileType === "firm-file")
                        await handleDownload(fileName, response.url);
                      else downloadFileLink(fileName, response.url);
                    } catch (error) {
                      toast.error(
                        fileOperationMessage(error, "Unable to download file"),
                      );
                    }
                  } else await handleDownload(fileName, fileUrl);
                }
              }}
              disabled={isLoading || !fileUrl}
            >
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          )}
          {showDownload && (
            <Button
              variant="outline"
              size="icon"
              type="button"
              className="h-6 w-6 text-blue-500 hover:text-blue-600"
              onClick={(e) => {
                e.stopPropagation();
                if (fileUrl && fileName) {
                  handlePreviewFile(fileName, fileUrl);
                }
              }}
              title="Open"
            >
              <SquareArrowOutUpRight className="h-4 w-4" />
            </Button>
          )}
          {onEdit && (
            <Button
              variant="outline"
              size="icon"
              type="button"
              className="text-muted-foreground hover:text-foreground h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </CardWrapper>
  );
}
